import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle, Copy, ArrowSquareOut, Bank, DeviceMobile,
  Money, Wallet, CreditCard, Package,
} from "@phosphor-icons/react";
import { myApi } from "../../../api";
import MyOrderPaymentPaypal from "../../MyOrderPaymentPaypal";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);

/**
 * Renders the instructions appropriate for the confirmed order based on the
 * selected payment method. Expects `data` to be the raw obj returned by
 * /checkout/confirm (gerais + ID_Encomenda + method-specific fields).
 */
export default function PaymentResult({ paymentMethod, data, total, currency = "EUR" }) {
  const idEncomenda = data?.ID_Encomenda ?? data?.idEncomenda ?? data?.encomenda?.ID_Encomenda;
  const gerais = data?.encomenda?.gerais || data?.gerais || data || {};

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-6" data-testid="payment-result">
      <div className="flex items-start gap-3 mb-5 pb-5 border-b border-[#E5E7EB]">
        <div className="w-11 h-11 rounded-full bg-green-100 text-green-700 flex items-center justify-center shrink-0">
          <CheckCircle size={22} weight="fill" />
        </div>
        <div>
          <h2 className="text-xl font-black tracking-tighter text-[#0F1829]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Encomenda registada!
          </h2>
          <p className="text-sm text-[#6B7280] mt-1">
            Nº da encomenda: <span className="font-semibold text-[#0F1829]" data-testid="payment-result-order-id">#{idEncomenda}</span>
            · Total: <span className="font-semibold text-[#0F1829]">{fmt(total, currency)}</span>
          </p>
        </div>
      </div>

      {paymentMethod === "MB" && <MBInstructions gerais={gerais} currency={currency} />}
      {paymentMethod === "MW" && <MBWayInstructions gerais={gerais} idEncomenda={idEncomenda} />}
      {paymentMethod === "TB" && <TBInstructions total={total} currency={currency} />}
      {paymentMethod === "PP" && <PayPalInstructions idEncomenda={idEncomenda} hashEncomenda={data?.HashEncomenda || data?.hashEncomenda} total={total} currency={currency} />}
      {paymentMethod === "CT" && <EbanxInstructions idEncomenda={idEncomenda} />}
      {paymentMethod === "CP" && <CofidisInstructions idEncomenda={idEncomenda} />}

      <div className="mt-6 pt-5 border-t border-[#E5E7EB] flex flex-col sm:flex-row gap-2">
        <Link to={`/my/encomendas/${idEncomenda}`}
          className="flex-1 text-center px-4 py-2.5 bg-[#0F1829] text-white rounded-md text-sm font-medium hover:bg-[#002480] transition-colors flex items-center justify-center gap-2"
          data-testid="payment-result-order-link">
          <Package size={16} /> Ver detalhe da encomenda
        </Link>
        <Link to="/my" className="flex-1 text-center px-4 py-2.5 text-[#6B7280] hover:bg-[#F3F4F6] rounded-md text-sm font-medium transition-colors"
          data-testid="payment-result-home-link">
          Voltar ao início
        </Link>
      </div>
    </div>
  );
}

// ─── Per-method instruction blocks ─────────────────────────────────────

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("CopyField: clipboard write failed", e);
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-[#F5F5F2] rounded-lg border border-[#E5E7EB]">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#6B7280]">{label}</p>
        <p className="text-base font-bold text-[#0F1829] tracking-wider" data-testid={testid}>{value}</p>
      </div>
      <button type="button" onClick={copy}
        className="px-2 py-1.5 rounded-md text-[#6B7280] hover:text-[#0F1829] hover:bg-white transition-colors shrink-0 flex items-center gap-1.5 text-xs"
        data-testid={`${testid}-copy`}>
        <Copy size={14} /> {copied ? "Copiado!" : "Copiar"}
      </button>
    </div>
  );
}

function MBInstructions({ gerais, currency }) {
  const entidade = gerais.multibancoMBEntidade || gerais.Entidade || gerais.entidade;
  const referencia = gerais.multibancoMBReferencia || gerais.Referencia || gerais.referencia;
  const valor = Number(gerais.multibancoMB || gerais.valor || 0);

  if (!entidade || !referencia) {
    return (
      <p className="text-sm text-[#6B7280]" data-testid="payment-mb-pending">
        A referência Multibanco está a ser gerada. Consulte o detalhe da encomenda em alguns instantes.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="payment-mb-block">
      <div className="flex items-center gap-2 text-[#002480] mb-2">
        <Bank size={18} />
        <p className="text-sm font-semibold">Pagamento por Referência Multibanco</p>
      </div>
      <CopyField label="Entidade" value={entidade} testid="payment-mb-entidade" />
      <CopyField label="Referência" value={referencia} testid="payment-mb-ref" />
      <CopyField label="Valor" value={fmt(valor, currency)} testid="payment-mb-valor" />
      <p className="text-xs text-[#6B7280] pt-2">Use estes dados em qualquer caixa Multibanco ou homebanking. A encomenda será processada após confirmação automática do pagamento.</p>
    </div>
  );
}

function MBWayInstructions({ gerais, idEncomenda }) {
  const tel = gerais.mbwayTelemovel || gerais.numero || gerais.Telemovel;
  const displayNumber = tel ? String(tel) : "(telemóvel indicado)";
  return (
    <div data-testid="payment-mbway-block">
      <div className="flex items-center gap-2 text-[#002480] mb-2">
        <DeviceMobile size={18} />
        <p className="text-sm font-semibold">Autorize o pagamento via MBWay</p>
      </div>
      <p className="text-sm text-[#374151] leading-relaxed">
        Foi enviada uma notificação para o telemóvel <b data-testid="payment-mbway-number">{displayNumber}</b> através da App MBWay.
      </p>
      <div className="mt-3 p-3 bg-[#F5F5F2] rounded-lg border border-[#E5E7EB] text-sm text-[#0F1829]">
        Texto da notificação: <b>PagamentoIHTP - {idEncomenda}</b>
      </div>
      <p className="text-xs text-[#6B7280] mt-3">Tem alguns minutos para confirmar na App MBWay. Após confirmado, a encomenda é processada automaticamente.</p>
    </div>
  );
}

function TBInstructions({ total, currency }) {
  return (
    <div className="space-y-2" data-testid="payment-tb-block">
      <div className="flex items-center gap-2 text-[#0F1829] mb-2">
        <Money size={18} />
        <p className="text-sm font-semibold">Pagamento por Transferência Bancária</p>
      </div>
      <CopyField label="IBAN" value="PT50 0007 0447 0000 8940 0041 7" testid="payment-tb-iban" />
      <CopyField label="SWIFT / BIC" value="BESCPTPL" testid="payment-tb-swift" />
      <CopyField label="Valor a transferir" value={fmt(total, currency)} testid="payment-tb-valor" />
      <p className="text-xs text-[#6B7280] pt-2">
        Após transferir, envie o comprovativo para <a href="mailto:encomendas@ihavethepower.net" className="text-[#002480] underline">encomendas@ihavethepower.net</a> indicando o nº da encomenda.
      </p>
    </div>
  );
}

function PayPalInstructions({ idEncomenda, hashEncomenda, total, currency }) {
  // Renderiza os Smart Buttons directamente neste ecrã para o utilizador
  // poder pagar sem ter de navegar para o detalhe da encomenda. Após
  // sucesso, o callback `onPaid` mostra confirmação inline.
  return (
    <div data-testid="payment-pp-block">
      <div className="flex items-center gap-2 text-[#003087] mb-3">
        <Wallet size={18} />
        <p className="text-sm font-semibold">Concluir o pagamento via PayPal</p>
      </div>
      <p className="text-sm text-[#374151] leading-relaxed mb-4">
        Use a sua conta PayPal, cartão de débito/crédito ou Apple Pay (Safari/iOS) para concluir esta encomenda.
      </p>
      <MyOrderPaymentPaypal
        orderId={idEncomenda}
        hashEncomenda={hashEncomenda}
        total={total}
        currency={currency}
      />
      <Link to={`/my/encomendas/${idEncomenda}`}
        className="mt-4 inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#0F1829]"
        data-testid="payment-pp-detail-link">
        Ver detalhe da encomenda <ArrowSquareOut size={12} />
      </Link>
    </div>
  );
}

function EbanxInstructions({ idEncomenda }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!idEncomenda) { setLoading(false); return; }
    myApi.shopEbanxUrl({ ID_Encomenda: idEncomenda })
      .then((r) => setUrl(r?.url || r?.obj?.url || r?.checkoutURL || ""))
      .catch(() => setUrl(""))
      .finally(() => setLoading(false));
  }, [idEncomenda]);

  return (
    <div data-testid="payment-ct-block">
      <div className="flex items-center gap-2 text-[#0F1829] mb-2">
        <CreditCard size={18} />
        <p className="text-sm font-semibold">Pagamento por Cartão de Crédito (Ebanx)</p>
      </div>
      {loading ? (
        <p className="text-sm text-[#6B7280]">A gerar link de pagamento…</p>
      ) : url ? (
        <a href={url} target="_blank" rel="noopener noreferrer"
          data-testid="payment-ct-link"
          className="inline-flex items-center gap-2 px-3 py-2 bg-[#0F1829] text-white rounded-md text-sm font-medium hover:bg-[#002480] transition-colors">
          Ir para página segura Ebanx <ArrowSquareOut size={14} />
        </a>
      ) : (
        <p className="text-sm text-[#6B7280]">Não foi possível gerar o link Ebanx automaticamente. Consulte o detalhe da encomenda.</p>
      )}
    </div>
  );
}

function CofidisInstructions({ idEncomenda }) {
  return (
    <div data-testid="payment-cp-block">
      <div className="flex items-center gap-2 text-[#0F1829] mb-2">
        <Wallet size={18} />
        <p className="text-sm font-semibold">Pagamento Cofidis Pay</p>
      </div>
      <p className="text-sm text-[#374151] leading-relaxed">
        Aceda ao detalhe da encomenda para finalizar a subscrição Cofidis Pay (até 12x sem juros).
      </p>
      <Link to={`/my/encomendas/${idEncomenda}`}
        className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-[#0F1829] text-white rounded-md text-sm font-medium hover:bg-[#002480] transition-colors"
        data-testid="payment-cp-continue">
        Ver detalhe <ArrowSquareOut size={14} />
      </Link>
    </div>
  );
}
